import { useCallback, useMemo, useState } from "react";
import {
  useGetUserFavoritesQuery,
  useAddFavoriteMutation,
  useRemoveFavoriteMutation,
  GetUserFavoritesDocument,
  GetUserFavoritesQuery,
} from "@/lib/graphql/types-and-hooks";
import { useWorkspaceId } from "./use-workspace-id";
import { isLovablePreview } from "@/lib/utils/environment";
import { reportFavoriteKey } from "@/lib/reports/report-route-id";
import type { FavoriteItem } from "@/contexts/FavoritesContext";

// ── Type mapping between frontend FavoriteItem types and backend entity types ──

export const FRONTEND_TO_BACKEND: Record<FavoriteItem["type"], string> = {
  person: "CONTACT",
  company: "ACCOUNT",
  deal: "DEAL",
  query: "AI_QUERY",
  report: "REPORT",
};

export const BACKEND_TO_FRONTEND: Record<string, FavoriteItem["type"]> = {
  CONTACT: "person",
  ACCOUNT: "company",
  DEAL: "deal",
  AI_QUERY: "query",
  REPORT: "report",
};

// ── Preview mode seed data ──

const SEED_FAVORITES: FavoriteItem[] = [
  { id: "1", type: "person", name: "Sarah Chen", subtitle: "VP of Engineering · Stripe" },
  { id: "1", type: "deal", name: "Payment Integration", subtitle: "Stripe · $85,000" },
  { id: "4", type: "company", name: "Datadog", subtitle: "Cloud-scale monitoring and analytics" },
  {
    id: "seed-4",
    type: "query",
    name: "Deals closing this month",
    subtitle: "Saved AI Prompt",
    query: "What deals are closing this month?",
    response:
      "Based on your pipeline, you have 2 deals expected to close this month: Payment Integration (Stripe, $85,000, Mar 15) and Annual Contract (Shopify, $42,000, Mar 20). Total potential revenue: $127,000.",
  },
];

// ── Helpers ──

/** Convert a backend UserFavorite row to the frontend FavoriteItem shape. */
export function toFavoriteItem(row: {
  id?: number | null;
  entityType?: string | null;
  entityId?: string | null;
  extraData?: any;
}): FavoriteItem {
  const meta = typeof row.extraData === "string" ? JSON.parse(row.extraData) : row.extraData ?? {};
  return {
    id: row.entityId ?? String(row.id),
    type: BACKEND_TO_FRONTEND[row.entityType ?? ""] ?? "person",
    name: meta.name ?? "",
    subtitle: meta.subtitle ?? "",
    query: meta.query,
    response: meta.response,
    reportTemplate: meta.reportTemplate,
    reportFilters: meta.reportFilters,
    reportDescription: meta.reportDescription,
    customBlocks: meta.customBlocks,
    reportDefinitionId: meta.reportDefinitionId,
    domain: meta.domain,
  };
}

/** Build the extraData JSONB payload from a FavoriteItem. */
export function toExtraData(item: FavoriteItem): string {
  const meta: Record<string, unknown> = {
    name: item.name,
    subtitle: item.subtitle,
  };
  if (item.query !== undefined) meta.query = item.query;
  if (item.response !== undefined) meta.response = item.response;
  if (item.reportTemplate !== undefined) meta.reportTemplate = item.reportTemplate;
  if (item.reportFilters !== undefined) meta.reportFilters = item.reportFilters;
  if (item.reportDescription !== undefined) meta.reportDescription = item.reportDescription;
  // Custom reports are nothing but their ordered block list — dropping this
  // makes a saved custom report reopen empty.
  if (item.customBlocks !== undefined) meta.customBlocks = item.customBlocks;
  // The v2 pointer — dropping it demotes the favorite back to a hidden v1 blob.
  if (item.reportDefinitionId !== undefined) meta.reportDefinitionId = item.reportDefinitionId;
  if (item.domain !== undefined) meta.domain = item.domain;
  return JSON.stringify(meta);
}

/**
 * Identity of a favorite for matching purposes.
 *
 * Report favorites are keyed on whatever id shape was current when they were
 * starred: v2 reports now carry a Relay global id, but rows saved before that
 * switch hold the bare Postgres pk. Collapsing both to the pk keeps a
 * pre-switch star lit — otherwise the report reads as unstarred and starring
 * it again writes a second row that the un-star can never reach.
 */
function favoriteKey(id: string, type: FavoriteItem["type"]): string {
  return `${type}:${type === "report" ? reportFavoriteKey(id) : id}`;
}

function findFavorite(
  favorites: FavoriteItem[],
  id: string,
  type: FavoriteItem["type"],
): FavoriteItem | undefined {
  const key = favoriteKey(id, type);
  return favorites.find((f) => favoriteKey(f.id, f.type) === key);
}

// ── Hook ──

export interface UseFavoritesReturn {
  favorites: FavoriteItem[];
  isFavorite: (id: string, type?: FavoriteItem["type"]) => boolean;
  toggleFavorite: (item: FavoriteItem) => void;
  upsertFavorite: (item: FavoriteItem) => void;
  removeFavorite: (id: string, type: FavoriteItem["type"]) => void;
  loading: boolean;
}

/** Preview-mode implementation: in-memory state with seed data. */
function usePreviewFavorites(): UseFavoritesReturn {
  const [favorites, setFavorites] = useState<FavoriteItem[]>(SEED_FAVORITES);

  const isFavorite = useCallback(
    (id: string, type?: FavoriteItem["type"]) => {
      if (type) return findFavorite(favorites, id, type) !== undefined;
      return favorites.some((f) => f.id === id);
    },
    [favorites],
  );

  const toggleFavorite = useCallback((item: FavoriteItem) => {
    setFavorites((prev) => {
      const existing = findFavorite(prev, item.id, item.type);
      if (existing) return prev.filter((f) => f !== existing);
      return [...prev, item];
    });
  }, []);

  const upsertFavorite = useCallback((item: FavoriteItem) => {
    setFavorites((prev) => {
      const existing = findFavorite(prev, item.id, item.type);
      if (!existing) return [...prev, item];
      return prev.map((f) => (f === existing ? item : f));
    });
  }, []);

  const removeFavorite = useCallback((id: string, type: FavoriteItem["type"]) => {
    setFavorites((prev) => {
      const existing = findFavorite(prev, id, type);
      return existing ? prev.filter((f) => f !== existing) : prev;
    });
  }, []);

  return { favorites, isFavorite, toggleFavorite, upsertFavorite, removeFavorite, loading: false };
}

/** Production-mode implementation: backed by GraphQL with optimistic cache updates. */
function useBackendFavorites(): UseFavoritesReturn {
  const workspaceId = useWorkspaceId();

  const { data, loading } = useGetUserFavoritesQuery({
    variables: { workspaceId: workspaceId! },
    skip: isLovablePreview() || !workspaceId,
    fetchPolicy: "cache-and-network",
  });

  const [addFavoriteMutation] = useAddFavoriteMutation();
  const [removeFavoriteMutation] = useRemoveFavoriteMutation();

  const favorites: FavoriteItem[] = useMemo(
    () => (data?.userFavorites ?? []).filter(Boolean).map((row) => toFavoriteItem(row!)),
    [data],
  );

  const isFavorite = useCallback(
    (id: string, type?: FavoriteItem["type"]) => {
      if (type) return findFavorite(favorites, id, type) !== undefined;
      return favorites.some((f) => f.id === id);
    },
    [favorites],
  );

  const addToCache = useCallback(
    (item: FavoriteItem) => {
      if (!workspaceId) return;
      addFavoriteMutation({
        variables: {
          input: {
            workspaceId,
            entityType: FRONTEND_TO_BACKEND[item.type],
            entityId: item.id,
            extraData: toExtraData(item),
          },
        },
        update(cache, { data: result }) {
          const fav = result?.addFavorite?.favorite;
          if (!fav) return;
          const existing = cache.readQuery<GetUserFavoritesQuery>({
            query: GetUserFavoritesDocument,
            variables: { workspaceId },
          });
          if (!existing) return;
          // Remove any existing entry for the same entity (upsert), then prepend
          const filtered = (existing.userFavorites ?? []).filter(
            (f) => !(f?.entityId === fav.entityId && f?.entityType === fav.entityType),
          );
          cache.writeQuery({
            query: GetUserFavoritesDocument,
            variables: { workspaceId },
            data: { userFavorites: [fav, ...filtered] },
          });
        },
      });
    },
    [workspaceId, addFavoriteMutation],
  );

  const removeFromCache = useCallback(
    (id: string, type: FavoriteItem["type"]) => {
      if (!workspaceId) return;
      const entityType = FRONTEND_TO_BACKEND[type];
      removeFavoriteMutation({
        variables: {
          input: { workspaceId, entityType, entityId: id },
        },
        update(cache) {
          const existing = cache.readQuery<GetUserFavoritesQuery>({
            query: GetUserFavoritesDocument,
            variables: { workspaceId },
          });
          if (!existing) return;
          cache.writeQuery({
            query: GetUserFavoritesDocument,
            variables: { workspaceId },
            data: {
              userFavorites: (existing.userFavorites ?? []).filter(
                (f) => !(f?.entityId === id && f?.entityType === entityType),
              ),
            },
          });
        },
      });
    },
    [workspaceId, removeFavoriteMutation],
  );

  const toggleFavorite = useCallback(
    (item: FavoriteItem) => {
      // Delete the row as it is actually stored — a pre-switch report favorite
      // holds the bare pk, so removing by item.id would miss it and leave the
      // star lit.
      const existing = findFavorite(favorites, item.id, item.type);
      if (existing) {
        removeFromCache(existing.id, existing.type);
      } else {
        addToCache(item);
      }
    },
    [favorites, addToCache, removeFromCache],
  );

  const upsertFavorite = useCallback(
    (item: FavoriteItem) => {
      addToCache(item);
    },
    [addToCache],
  );

  const removeFavorite = useCallback(
    (id: string, type: FavoriteItem["type"]) => {
      removeFromCache(findFavorite(favorites, id, type)?.id ?? id, type);
    },
    [favorites, removeFromCache],
  );

  return { favorites, isFavorite, toggleFavorite, upsertFavorite, removeFavorite, loading };
}

/** Main hook — delegates to preview or backend implementation. */
export function useFavoritesBackend(): UseFavoritesReturn {
  const preview = usePreviewFavorites();
  const backend = useBackendFavorites();
  return isLovablePreview() ? preview : backend;
}
